// Per-type field relevance for the product configurator + detail editor
// (issue #710) and the structured product-name generator (issue #707).
// The visibility matrix mirrors backend/utils/productQuality.js — never hide a
// field the quality scorer counts toward that type's completeness.

export const ASPECT_RATIO_OPTIONS = ['16:9', '16:10', '5:4'];
export const MOUNTING_OPTIONS = ['Panel mount', 'Bracket', 'VESA', 'Flush mount', 'Hinge', 'Free mounting'];

// field → the product types it applies to. A field not listed applies to all.
const FIELD_TYPES = {
  size: ['MON', 'DIU', 'PAN', 'ACC'],
  touch: ['MON', 'DIU', 'PAN'],
  powerSupply: ['MON', 'ELU', 'BOX'],
  bonding: ['MON', 'DIU', 'PAN'],
  brightness: ['MON', 'DIU', 'PAN'],
  interfaces: ['MON', 'ELU', 'BOX'],
  aspectRatio: ['MON', 'DIU', 'PAN'],
  nvis: ['MON', 'DIU', 'PAN'],
  resolution: ['MON', 'DIU', 'PAN'],
  ipRating: ['MON', 'DIU', 'PAN'],
  linkedDiu: ['MON'],
  linkedElu: ['MON', 'BOX'],
  parentMon: ['DIU', 'ELU'],
  drawingReference: ['DIU', 'ELU'],
  datasheetConfirmed: ['MON', 'BOX', 'ACC'],
  datasheetUrl: ['MON', 'BOX', 'ACC'],
  serialNumberLayout: ['MON', 'DIU', 'ELU', 'BOX'],
  biosReady: ['MON', 'ELU', 'BOX'],
  certifications: ['MON', 'BOX'],
  mounting: ['MON', 'PAN', 'ACC'],
  operatingVoltage: ['MON', 'ELU', 'BOX'],
  bomLink: ['MON', 'DIU', 'ELU', 'BOX'],
};

export function fieldVisible(field, type) {
  const types = FIELD_TYPES[field];
  return !types || types.includes(String(type || 'MON').toUpperCase());
}

function titleize(key) {
  return String(key).split(/[_\s]+/).map(w => (w ? w[0].toUpperCase() + w.slice(1) : '')).join(' ');
}

// Deterministic name from the structured variables — the de-facto grammar of
// the master part-number sheet per family, with tokens the backend text parser
// (utils/productFacets.js) round-trips. Returns '' when the inputs are too
// thin to name; the caller keeps the manual name in that case.
export function generateProductName(p) {
  const type = String(p.type || 'MON').toUpperCase();
  const size = (p.size || '').trim();
  const hb = p.brightness === 'High bright';
  const nvis = p.nvis === true;
  const power = (p.powerSupply || '').trim();
  const aspect = (p.aspectRatio || '').trim();
  const bonding = (p.bonding || '').trim();
  const ip = (p.ipRating || '').trim();

  if (type === 'MON') {
    if (!size) return '';
    if (p.family === 'wave_performance') {
      let n = 'Wave PERFORMANCE';
      if (p.touch === true) n += ' Multitouch';
      n += ` ${size}`;
      if (hb) n += ' HIGH BRIGHT';
      if (nvis) n += ' NVIS';
      if (power) n += ` - ${power}${power.includes('/') ? ' MPSU' : ''}`;
      if (ip) n += ` ${ip}`;
      return n;
    }
    if (p.family === 'wave_xl') {
      // touch === null stays unnamed — a generated 'Non-Touch' would teach the
      // backend text parser a fact nobody entered.
      let n = `Wave XL${p.touch === true ? ' Multitouch' : p.touch === false ? ' Non-Touch' : ''} Monitor ${size}`;
      if (power) n += ` - ${power}`;
      if (bonding) n += `, ${bonding} Bonding`;
      return n;
    }
    // wave_ii grammar doubles as the generic fallback for other families
    const fam = !p.family || p.family === 'wave_ii' ? 'Wave II' : titleize(p.family);
    let n = `${fam} ${p.touch === true ? 'Multitouch' : 'Monitor'}`;
    if (hb) n += ' High Bright';
    if (nvis) n += ' NVIS';
    n += ` ${size}`;
    if (aspect) n += ` (${aspect})`;
    if (power) n += ` - ${power}`;
    if (ip) n += ` - ${ip}`;
    return n;
  }
  if (type === 'DIU') {
    if (!size) return '';
    const touchToken = p.touch === true ? 'Touch ' : p.touch === false ? 'Non-Touch ' : '';
    return `${nvis ? 'NVIS ' : ''}${size} Gen2 DIU ${touchToken}${hb ? 'HB' : 'STD'} Brightness`;
  }
  if (type === 'ELU') {
    if (!power) return '';
    if (p.family === 'wave_ii') {
      return `Wave II ELU — ${power}${p.interfaces ? `, ${p.interfaces}` : ''}`;
    }
    let n = `WP ELU: ${power}`;
    if (p.interfaces) n += `, ${p.interfaces}`;
    return n;
  }
  if (type === 'BOX') {
    // The BOX grammar is Wave Performance specific — other families keep manual names.
    if (!power || (p.family && p.family !== 'wave_performance')) return '';
    return `Wave PERFORMANCE BOX - ${power}`;
  }
  return '';
}

// Types the generator has a grammar for — the UI hides the generate control elsewhere.
export const NAME_GENERATOR_TYPES = ['MON', 'DIU', 'ELU', 'BOX'];

// The structured fields that feed the generator — editing one of these while
// auto-naming is on regenerates the name.
export const NAME_INPUT_FIELDS = ['type', 'family', 'size', 'touch', 'brightness', 'nvis', 'powerSupply', 'aspectRatio', 'bonding', 'interfaces', 'ipRating'];
